import { startCase } from 'lodash'
import moment from 'moment'
import { dateOptionLabel } from 'helpers/date-option-range'
import { activeFilterEntries } from 'api/filter-tree'
import { formatCurrency, formatNumber } from 'helpers/chart-formatters'

// Date pickers save a bare date or a full timestamp; either reads as e.g. "Jan 1, 2024".
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/
const NUMERIC_FILTER_TYPES = ['min_max', 'min_max_percent', 'time_in_seconds', 'greater_than']
const SECONDS_PER_DAY = 86400

// A value as its column shows it: $1,000 for money, 25% for a percent (saved as 0.25), 3 days for
// a time in queue (saved in seconds), 1,000 for any other number, Jan 1, 2024 for a date.
export const displayValue = (value, column = null) => {
  const text = String(value)
  if (ISO_DATE.test(text)) return moment.utc(text).format('MMM D, YYYY')
  const number = Number(text)
  if (text.trim() === '' || !Number.isFinite(number) || !NUMERIC_FILTER_TYPES.includes(column?.filter_type)) return text
  if (column.filter_type === 'min_max_percent') return `${formatNumber(number * 100, 2)}%`
  if (column.filter_type === 'time_in_seconds') {
    const days = number / SECONDS_PER_DAY
    return `${formatNumber(days, 2)} ${days === 1 ? 'day' : 'days'}`
  }
  if (['money', 'two_digit_money'].includes(column.display_as)) return formatCurrency(number, !Number.isInteger(number))
  return formatNumber(number, 2)
}

const isOperatorKey = (key) => key.endsWith('_operator') || key.endsWith('_option')

// Date options whose dates are the leaf's min/max, which the bounds already read as.
const BOUND_OPTIONS = ['range', 'any', 'between', 'less', 'greater', 'on']

// Choice verbs read as a pick from several values, so names that themselves contain commas
// never run together.
export const choiceVerb = (count, exclude = false) => {
  if (exclude) return count > 1 ? 'is none of' : 'is not'
  return count > 1 ? 'is any of' : 'is'
}

const OPERATOR_VERBS = { equals: 'is', not_equals: 'is not' }

const operatorVerb = (key, operator) =>
  (key.endsWith('_option') && dateOptionLabel(operator)) ||
  OPERATOR_VERBS[operator] ||
  startCase(String(operator)).toLowerCase()

const boundClauses = (min, max, isDate) => {
  if (min !== undefined && max !== undefined) {
    if (min === max) return [{ verb: isDate ? 'on' : 'is', values: [min] }]
    return [{ verb: 'between', values: [`${min} and ${max}`] }]
  }
  if (min !== undefined) return [{ verb: '≥', values: [min] }]
  return max !== undefined ? [{ verb: '≤', values: [max] }] : []
}

// A bare value is "contains" on basic text and a floor on greater-than (the API applies >=).
const bareValueVerb = (filterType, count, mode) => {
  if (filterType === 'basic') return 'contains'
  if (filterType === 'greater_than') return '≥'
  return choiceVerb(count, mode === 'exclude')
}

// A leaf's API params as clauses, e.g. [{ verb: 'starts with', values: ['53'] }] or
// [{ verb: 'between', values: ['100 and 500'] }]. Reads the param names every
// filter type shares. The column (or just its filter_type) settles what a bare value means (basic
// text means "contains") and how values read.
export const describeFilter = (filter = {}, columnOrFilterType = null) => {
  const column = typeof columnOrFilterType === 'string' ? { filter_type: columnOrFilterType } : columnOrFilterType
  const filterType = column?.filter_type
  const entries = activeFilterEntries(filter)
  const [operatorKey, operator] = entries.find(([key]) => isOperatorKey(key)) || []
  const mode = entries.find(([key]) => key.endsWith('_mode'))?.[1]
  const values = []
  let min
  let max
  let isDate = filterType === 'date'
  const show = (value) => {
    isDate ||= ISO_DATE.test(String(value))
    return displayValue(value, column)
  }

  entries.forEach(([key, value]) => {
    if (isOperatorKey(key) || key.endsWith('_mode')) return
    if (/(^|_)min_/.test(key)) min = show(value)
    else if (/(^|_)max_/.test(key)) max = show(value)
    else values.push(...[].concat(value).map(show))
  })

  const clauses = boundClauses(min, max, isDate)
  if (operator && !BOUND_OPTIONS.includes(operator)) {
    clauses.unshift({ verb: operatorVerb(operatorKey, operator), values })
  } else if (values.length) {
    clauses.unshift({ verb: bareValueVerb(filterType, values.length, mode), values })
  }
  return clauses
}

// One line for a filter's button: values quoted once there is more than one.
export const summarizeClauses = (clauses) =>
  clauses
    .map(({ verb, values }) =>
      [verb, values.length > 1 ? values.map((v) => `"${v}"`).join(', ') : values[0]].filter(Boolean).join(' ')
    )
    .join(', ')

export const summarizeFilter = (filter, column) => summarizeClauses(describeFilter(filter, column))

// A leaf's one-line summary. Columns whose params are ids (account segments) or a flag (analytics)
// bring their own, since the generic reading would show the raw values.
export const summarizeLeaf = (leaf, column) =>
  column?.summarizeLeaf ? column.summarizeLeaf(leaf) : summarizeFilter(leaf.filter, column)

// A whole tree as one sentence, e.g. "Invoice Number starts with 53 OR (Amount ≥ 100 AND Vendor is ACME)".
// `columnFor(leaf)` returns the leaf's filter column, for its label and type.
export const describeTree = (node, columnFor, nested = false) => {
  if (!node) return ''
  if (Array.isArray(node.children)) {
    const parts = node.children.map((child) => describeTree(child, columnFor, true)).filter(Boolean)
    const joined = parts.join(` ${String(node.op).toUpperCase()} `)
    return nested && parts.length > 1 ? `(${joined})` : joined
  }
  return describeLeaf(node, columnFor)
}

// One filter as it reads to a person, e.g. "Vendor Name is Acme Supply"; empty when it filters nothing.
export const describeLeaf = (node, columnFor) => {
  const column = columnFor(node)
  const summary = summarizeLeaf(node, column)
  return summary ? [column?.display_name || column?.name, summary].filter(Boolean).join(' ') : ''
}

// The tree as nested lists for display: { op, items: [text | outline] }, empty filters and groups
// dropped and single-item groups unwrapped, or null when nothing filters.
export const outlineTree = (node, columnFor) => {
  if (!node) return null
  if (!Array.isArray(node.children)) return describeLeaf(node, columnFor) || null
  const items = node.children.map((child) => outlineTree(child, columnFor)).filter(Boolean)
  if (items.length === 0) return null
  return items.length === 1 ? items[0] : { op: node.op, items }
}
